import React, { useMemo } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getOverdueReport } from '../../data/reportHelpers';
import SummaryStatBar from './SummaryStatBar';
import ExportBar from './ExportBar';

const { FiAlertTriangle, FiClock, FiUsers, FiCalendar } = FiIcons;

const COLUMNS = [
  { key: 'learnerName', label: 'Learner' },
  { key: 'email', label: 'Email' },
  { key: 'department', label: 'Department' },
  { key: 'manager', label: 'Manager' },
  { key: 'campaign', label: 'Campaign' },
  { key: 'campaignType', label: 'Type' },
  { key: 'dueDate', label: 'Due Date' },
  { key: 'completion', label: 'Completion %' },
  { key: 'daysOverdue', label: 'Days Overdue' },
];

const urgencyConfig = (days) => {
  if (days > 30) return { color: 'bg-red-100 text-red-700 border-red-200', label: 'Critical' };
  if (days > 14) return { color: 'bg-orange-100 text-orange-700 border-orange-200', label: 'High' };
  if (days > 7) return { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Medium' };
  return { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: 'Low' };
};

const OverdueTrainingReport = ({ filters, raw }) => {
  const data = useMemo(() => getOverdueReport(raw, filters), [raw, filters]);

  const depts = [...new Set(data.map(r => r.department))];
  const stats = [
    { label: 'Overdue Records', value: data.length, icon: FiAlertTriangle, iconColor: 'bg-red-50 text-red-600', border: 'border-red-100' },
    { label: 'Affected Learners', value: [...new Set(data.map(r => r.learnerId))].length, icon: FiUsers, iconColor: 'bg-orange-50 text-orange-600', border: 'border-orange-100' },
    { label: 'Departments Affected', value: depts.length, icon: FiUsers, iconColor: 'bg-amber-50 text-amber-600', border: 'border-amber-100' },
    { label: 'Avg. Days Overdue', value: data.length ? Math.round(data.reduce((a, b) => a + b.daysOverdue, 0) / data.length) : 0, icon: FiClock, iconColor: 'bg-violet-50 text-violet-600', border: 'border-violet-100' },
    { label: 'Critical (>30 days)', value: data.filter(r => r.daysOverdue > 30).length, icon: FiAlertTriangle, iconColor: 'bg-rose-50 text-rose-600', border: 'border-rose-100' },
    { label: 'Avg. Completion', value: `${data.length ? Math.round(data.reduce((a, b) => a + b.completion, 0) / data.length) : 0}%`, icon: FiCalendar, iconColor: 'bg-teal-50 text-teal-600', border: 'border-teal-100' },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <SafeIcon icon={FiAlertTriangle} className="text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-red-700">Overdue Training Alert</p>
          <p className="text-xs text-red-600 mt-0.5">
            {data.length} overdue training record{data.length !== 1 ? 's' : ''} require immediate attention.
            Managers should follow up with their direct reports.
          </p>
        </div>
      </div>
      <SummaryStatBar stats={stats} />
      <ExportBar reportTitle="Overdue Training Report" data={data} columns={COLUMNS} summaryStats={stats.map(s => ({ label: s.label, value: s.value }))} rowCount={data.length} />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <th className="p-4">Learner</th>
                <th className="p-4">Department / Manager</th>
                <th className="p-4">Campaign</th>
                <th className="p-4">Due Date</th>
                <th className="p-4">Completion</th>
                <th className="p-4">Days Overdue</th>
                <th className="p-4">Urgency</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {data.map((row, i) => {
                const urgency = urgencyConfig(row.daysOverdue);
                return (
                  <tr key={i} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4">
                      <p className="font-medium text-slate-800">{row.learnerName}</p>
                      <p className="text-xs text-slate-400">{row.email}</p>
                    </td>
                    <td className="p-4 text-xs text-slate-600">
                      <p>{row.department}</p>
                      <p className="text-slate-400">{row.manager}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-sm text-slate-700 max-w-40 truncate">{row.campaign}</p>
                      <p className="text-xs text-slate-400">{row.campaignType}</p>
                    </td>
                    <td className="p-4 text-xs text-slate-500">{row.dueDate}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-1.5">
                        <div className="w-12 bg-slate-100 rounded-full h-1.5">
                          <div className="h-1.5 rounded-full bg-orange-400" style={{ width: `${row.completion}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-slate-600">{row.completion}%</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`text-sm font-bold ${row.daysOverdue > 30 ? 'text-red-600' : row.daysOverdue > 14 ? 'text-orange-600' : 'text-amber-600'}`}>
                        {row.daysOverdue}d
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${urgency.color}`}>{urgency.label}</span>
                    </td>
                  </tr>
                );
              })}
              {data.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-400 text-sm">No overdue training records found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">{data.length} overdue records</div>
      </div>
    </div>
  );
};

export default OverdueTrainingReport;